'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { VariantProps } from 'tailwind-variants';

import EmailIcon from '@/fo/icons/base/icons/email.svg';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitInputBase } from '@/fo/ui/kit/input/base';

import { styles } from './UiSubscriptionSectionBase.styles';

type Variants = VariantProps<typeof styles>;

export type SubscriptionFormStatus = Variants['formStatus'];

export interface BrandImage {
  /** URL изображения */
  src: string;
  /** Альтернативный текст для изображения */
  alt: string;
}

export interface UiSubscriptionSectionBaseProps {
  /** Статус авторизации пользователя */
  loginStatus?: Variants['loginStatus'];
  /** Статус формы подписки */
  formStatus?: SubscriptionFormStatus;
  /** Процент скидки для отображения, по умолчанию 15 */
  discountPercentage?: number;
  /** Функция для подписки */
  onSubscribe?: (email: string) => Promise<void>;
  /** Текст ответа */
  errorText?: string;
}

/**
 * Компонент секции подписки с формой для сбора email и отображением скидки
 */
export function UiSubscriptionSectionBase({
  loginStatus,
  formStatus,
  onSubscribe,
  errorText,
  ...props
}: UiSubscriptionSectionBaseProps) {
  const t = useTranslations();
  const slots = styles({ loginStatus, formStatus });
  const [email, setEmail] = useState('');

  const onChangeEmail = (value: string) => {
    setEmail(value);
  };
  const onClickSubscribe = async () => {
    await onSubscribe?.(email);
  };

  const subscriptionButton = () => (
    <UiKitButtonBase
      className={slots.button()}
      onClick={onClickSubscribe}
      color="white"
    >
      <EmailIcon className={slots.icon()} />
    </UiKitButtonBase>
  );

  return (
    <div
      className={slots.base()}
      data-testid="subscription-section"
      {...props}
    >
      <div className={slots.wrap()}>
        <div className={slots.content()}>
          <div className={slots.title()}>
            {t('section.subscription.subscribeTitle')}
          </div>
          <div className={slots.formWrap()}>
            <div className={slots.form()}>
              <UiKitInputBase
                label="E-mail"
                name="email"
                value={email}
                onChange={onChangeEmail}
                error={formStatus === 'error'}
                helperText={formStatus === 'error' ? errorText : ''}
                icon={subscriptionButton()}
                white={true}
              />
            </div>
            <p className={slots.disclaimer()}>
              {t.rich('section.subscription.subscribeApproval', {
                link: (chunks) => (
                  <Link
                    className={slots.approvalLink()}
                    href="/privacy-policy"
                  >
                    {chunks}
                  </Link>
                )
              })}
            </p>
          </div>
          <p className={slots.successMessage()}>
            {t('section.subscription.successMessage')}
          </p>
        </div>
      </div>
    </div>
  );
}
